import { ReactNode } from 'react';
import { Link, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import AuthForm from '@/components/AuthForm';
import { useAuth } from '@/contexts/AuthContext';
import LandingPage from '@/pages/LandingPage';
import OnboardingPage from '@/pages/OnboardingPage';
import SellerStudioPage from '@/pages/SellerStudioPage';
import CustomerCheckoutPage from '@/pages/CustomerCheckoutPage';
import OpenCheckoutPage from '@/pages/OpenCheckoutPage';
import SettingsPage from '@/pages/SettingsPage';
import AccountSettings from '@/pages/settings/AccountSettings';
import WorkspaceSettings from '@/pages/settings/WorkspaceSettings';
import BillingSettings from '@/pages/settings/BillingSettings';
import PayoutsSettings from '@/pages/settings/PayoutsSettings';
import SubscriptionPage from '@/pages/SubscriptionPage';
import PaymentSuccessPage from '@/pages/PaymentSuccessPage';
import PaymentCancelledPage from '@/pages/PaymentCancelledPage';
import CreateWorkspacePage from '@/pages/CreateWorkspacePage';
import BrandLogo from '@/assets/logo.svg?react';

type WorkspaceRole = 'owner' | 'admin' | 'member';

const canAdminWorkspace = (role: WorkspaceRole | null): boolean => role === 'owner' || role === 'admin';

function AppShell({ children }: { children: ReactNode }) {
  const { userSettings } = useAuth();
  const workspaceName = userSettings?.activeWorkspace?.name;

  return (
    <div className="min-h-screen bg-[#f8f5ef]">
      <header className="sticky top-0 z-20 px-3 pt-3 sm:px-4">
        <div className="mx-auto flex w-full max-w-6xl items-center justify-between rounded-[1.1rem] border border-slate-200 bg-white px-3 py-3 shadow-[0_10px_30px_-22px_rgba(15,23,42,0.35)] sm:px-4">
          <Link to="/" className="flex items-center gap-2.5 rounded-xl px-1.5 py-1 text-slate-900 transition-colors hover:bg-slate-50">
            <span className="grid h-9 w-9 place-content-center rounded-xl border border-slate-200 bg-slate-950 shadow-sm">
              <BrandLogo className="h-5 w-5" />
            </span>
            <span className="font-sans text-[1.55rem] font-semibold tracking-[-0.03em] text-slate-900">LendLifted</span>
          </Link>
          <div className="flex items-center gap-2 sm:gap-3">
            <nav className="hidden items-center rounded-full border border-slate-200 bg-slate-100 px-1.5 py-1 text-[0.9rem] font-medium tracking-[-0.01em] text-slate-700 md:flex">
              <Link to="/onboarding" className="rounded-full px-4 py-2 transition hover:bg-white hover:text-slate-900">Connect + KYC</Link>
              <Link to="/studio" className="rounded-full bg-slate-900 px-4 py-2 text-white shadow-[0_8px_14px_-10px_rgba(15,23,42,0.65)] transition hover:bg-slate-800">Launch Quote</Link>
              <Link to="/settings" className="rounded-full px-4 py-2 transition hover:bg-white hover:text-slate-900">Settings</Link>
            </nav>
            <nav className="flex items-center gap-2 text-sm md:hidden">
              <Link to="/studio" className="rounded-full bg-slate-900 px-3 py-2 font-medium text-white transition hover:bg-slate-800">Studio</Link>
              <Link to="/settings" className="rounded-full border border-slate-200 bg-white px-3 py-2 font-medium text-slate-700 transition hover:bg-slate-50">Settings</Link>
            </nav>
            {workspaceName && (
              <span className="hidden rounded-full border border-slate-200 bg-white px-3 py-2 text-[0.7rem] font-semibold uppercase tracking-[0.08em] text-slate-600 lg:inline-flex">
                {workspaceName}
              </span>
            )}
          </div>
        </div>
      </header>
      <main className="pt-3">{children}</main>
    </div>
  );
}

function SellerGuard({ children, requireAdmin = false }: { children: ReactNode; requireAdmin?: boolean }) {
  const location = useLocation();
  const { currentUser, userSettings, isInitializing, isLoadingUserSettings } = useAuth();

  if (isInitializing || isLoadingUserSettings) {
    return (
      <div className="grid min-h-screen place-content-center">
        <Loader2 className="h-8 w-8 animate-spin text-slate-700" />
      </div>
    );
  }

  if (!currentUser || currentUser.isAnonymous) {
    return <Navigate to={`/auth?redirect=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  }

  const activeWorkspaceId = userSettings?.activeWorkspaceId;
  const role = userSettings?.workspaces?.find((ws) => ws.id === activeWorkspaceId)?.role as WorkspaceRole | undefined;

  if (!activeWorkspaceId) {
    return (
      <AppShell>
        <section className="mx-auto max-w-3xl px-4 pt-16 sm:px-6">
          <div className="rounded-3xl border border-slate-900/10 bg-white/90 p-8">
            <h1 className="font-serif text-3xl text-slate-900">Workspace required</h1>
            <p className="mt-3 text-slate-700">You need an active workspace before launching onboarding or financing quotes.</p>
            <div className="mt-6 flex flex-wrap gap-3">
              <button type="button" onClick={() => window.location.reload()} className="inline-flex rounded-full bg-slate-900 px-5 py-3 text-sm font-semibold text-white">Retry workspace check</button>
              <Link to="/create-workspace" className="inline-flex rounded-full border border-slate-300 px-5 py-3 text-sm font-semibold text-slate-700">Create a Workspace</Link>
            </div>
          </div>
        </section>
      </AppShell>
    );
  }

  if (requireAdmin && !canAdminWorkspace(role || null)) {
    return (
      <AppShell>
        <section className="mx-auto max-w-3xl px-4 pt-16 sm:px-6">
          <div className="rounded-3xl border border-slate-900/10 bg-white/90 p-8">
            <h1 className="font-serif text-3xl text-slate-900">Admin access required</h1>
            <p className="mt-3 text-slate-700">Only workspace owners and admins can manage Stripe Connect onboarding.</p>
          </div>
        </section>
      </AppShell>
    );
  }

  return <AppShell>{children}</AppShell>;
}

function AuthGuard({ children }: { children: ReactNode }) {
  const location = useLocation();
  const { currentUser, isInitializing } = useAuth();

  if (isInitializing) {
    return (
      <div className="grid min-h-screen place-content-center">
        <Loader2 className="h-8 w-8 animate-spin text-slate-700" />
      </div>
    );
  }

  if (!currentUser || currentUser.isAnonymous) {
    return <Navigate to={`/auth?redirect=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  }

  return <>{children}</>;
}

export default function App() {
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/auth" element={<AuthForm />} />
      <Route path="/create-workspace" element={<AuthGuard><CreateWorkspacePage /></AuthGuard>} />
      <Route path="/onboarding" element={<SellerGuard requireAdmin><OnboardingPage /></SellerGuard>} />
      <Route path="/dashboard" element={<Navigate to="/studio" replace />} />
      <Route path="/studio" element={<SellerGuard><SellerStudioPage /></SellerGuard>} />
      <Route path="/settings" element={<SellerGuard><SettingsPage /></SellerGuard>}>
        <Route path="account" element={<AccountSettings />} />
        <Route path="workspace" element={<WorkspaceSettings />} />
        <Route path="billing" element={<BillingSettings />} />
        <Route path="payouts" element={<PayoutsSettings />} />
      </Route>
      <Route path="/subscriptions" element={<SellerGuard><SubscriptionPage /></SellerGuard>} />
      <Route path="/payment-success" element={<SellerGuard><PaymentSuccessPage /></SellerGuard>} />
      <Route path="/payment-cancelled" element={<SellerGuard><PaymentCancelledPage /></SellerGuard>} />
      <Route path="/q/:token" element={<CustomerCheckoutPage />} />
      <Route path="/pay/:token" element={<CustomerCheckoutPage />} />
      <Route path="/open/:slug" element={<OpenCheckoutPage />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
